// @flow

import React from 'react';
import useSheet from 'app/jss';
import classNames from 'classnames';
import { isEmail } from 'validator';
import { partition, sortBy, noop } from 'lodash';
import { NOT_FRIEND } from 'app/constants/friendStatusTypes';
import { connect } from 'react-redux';
import t from 'app/utils/translate';
import searchFriendsSelector from 'app/selectors/friends/searchFriendsSelector';
import * as Enter from 'app/utils/animations/enter';
import * as Leave from 'app/utils/animations/leave';
import AnimatedGroup from 'app/components/commons/AnimatedGroup';
import InviteFriend from 'app/components/friends/banners/InviteFriend';
import FriendRow from 'app/components/friends/FriendRow';
import FriendRowExistingGroup from 'app/components/friends/FriendRowExistingGroup';
import FriendsRowList from 'app/components/friends/FriendsRowList';
import FriendsLoader from 'app/components/friends/common/FriendsLoader';
import FriendSlider from 'app/components/friends/common/FriendSlider';
import IconWatermark from 'app/components/friends/common/IconWatermark';
import { removeFriend } from 'app/actions/friendsActions';

type Props = {
  friends: { [id: number]: Friend },
  requests: { [id: number]: FriendRequest },
  classes: Object,
  searchResults?: Array<iFriendUser>,
  onFriendClick?: (user: iFriendUser) => void,
  searchId: string | number,
  onCreateFriendRequest?: () => void,
  onAddToGroup?: () => void,
  onRemoveFriend?: () => void,
  isSearching?: boolean,
  group?: FriendGroup,
  searchTerm?: string,
  isCreatingFriendRequests: Array<string>,
  members?: Array<number | string>,
  isNew?: boolean,
  className?: string,
};

type State = {
  isAsyncing: boolean,
  requestDone: boolean,
  showEmailUser: boolean,
  searchUser: iFriendUser,
};

class GroupContentsSearchResults extends React.Component {
  props: Props;
  state: State = {
    isAsyncing: false,
    requestDone: false,
  };

  static defaultProps = {
    searchResults: [],
    onFriendClick: undefined,
    isSearching: false,
    group: undefined,
    searchTerm: '',
    members: [],
    isNew: false,
    onCreateFriendRequest: noop,
    onAddToGroup: noop,
    onRemoveFriend: noop,
    className: undefined,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (
      this.props.isCreatingFriendRequests.length === 0 &&
      nextProps.isCreatingFriendRequests.length > 0
    ) {
      this.setState({ isAsyncing: false, requestDone: true });
      this.timeout = window.setTimeout(() => {
        this.setState({ requestDone: false });
      }, 3000);
    }

    const searchUser = this.state.searchUser;
    if (nextProps.searchResults !== this.props.searchResults && searchUser) {
      //  if our search user gets updated, such as sending a request
      // or removing it - update the currently selected user
      const newSearchUser = nextProps.searchResults.find(user => user.id === searchUser.id);
      this.setState({
        searchUser: newSearchUser,
      });
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.timeout);
  }

  onFriendClick = (friend) => {
    this.setState({ searchUser: friend });
  };

  inviteUser = (user: iFriendUser) => {
    const { searchTerm, group, onCreateFriendRequest } = this.props;

    this.setState({ isAsyncing: true });
    onCreateFriendRequest([searchTerm], (group || {}).id);
  };

  buildUserRows = (users: Array<iFriendUser>) => {
    const {
      isCreatingFriendRequests,
      isNew,
      members,
      onAddToGroup,
      onRemoveFriend,
      onCreateFriendRequest,
      group,
    } = this.props;
    if (isNew) {
      return users.map(user => (
        <FriendRow
          key={user.id}
          friend={user}
          members={members}
          isCreatingFriendRequest={isCreatingFriendRequests.includes(user.id)}
          onRowClick={this.onFriendClick}
          onAddToGroup={onAddToGroup}
          onRemoveFriend={onRemoveFriend}
          onCreateFriendRequest={onCreateFriendRequest}
        />
      ));
    }
    return users.map(user => (
      <FriendRowExistingGroup
        key={user.id}
        group={group}
        friend={user}
        members={members}
        onRowClick={this.onFriendClick}
      />
    ));
  };

  render() {
    const {
      classes,
      isSearching,
      searchResults,
      members,
      searchTerm,
      isCreatingFriendRequests,
      friends,
      requests,
      className,
    } = this.props;
    const { isAsyncing, requestDone } = this.state;

    // search users are only part of the information, so get the actual objects if we have them
    const mappedSearchResults = searchResults.map(user => friends[user.id] || requests[user.id] || user);
    const foundEmailRequest = requests[searchTerm];
    const substitutedResults = mappedSearchResults.concat(foundEmailRequest ? [foundEmailRequest] : []);
    const [aboveUsers, belowUsers] = partition(substitutedResults, user =>
      members.includes(user.id));

    const isCreating = isAsyncing && isCreatingFriendRequests.length > 0;
    const anyAbove = aboveUsers.length > 0;
    const anyBelow = anyAbove || belowUsers.length > 0;

    const showEmailUser = substitutedResults.length === 0 && !isSearching && isEmail(searchTerm);
    return (
      <div className={classNames(classes.searchResults, className)}>
        <FriendSlider friend={this.state.searchUser} onBack={() => this.onFriendClick(undefined)} />
        {showEmailUser && (
          <AnimatedGroup
            onAppear={Enter.grow(0.5, 0.3)}
            onLeave={Leave.disappear(0.5)}
            key="invite"
            className={classes.invite}
          >
            <InviteFriend
              friend={{ username: searchTerm, status: NOT_FRIEND }}
              isCreating={isCreating}
              isDone={requestDone}
              onConfirm={this.inviteUser}
            />
          </AnimatedGroup>
        )}
        {!showEmailUser && (
          <AnimatedGroup
            onEnter={Enter.appear(0.5, 0.5)}
            onLeave={Leave.disappear(0.3)}
            key="results"
            className={classes.search}
          >
            {isSearching && (
              <FriendsLoader showOverlay key="loader">
                {`${t('Searching for users')}...`}
              </FriendsLoader>
            )}
            {!anyBelow &&
              searchTerm.length > 0 &&
              !isSearching && (
                <div className={classes.noUsers} key="nousers">
                  <div className={classes.title}>{`${t('Sorry, no luck')}...`}</div>
                  <p className={classes.text}>{t("Couldn't find who you were looking for")}</p>
                  <IconWatermark className={classes.watermark} icon="icon-friend" />
                </div>
              )}
            <FriendsRowList key="above">
              {this.buildUserRows(sortBy(aboveUsers, 'sortOrder'))}
            </FriendsRowList>
            {anyAbove && anyBelow && <div className={classes.divider} key="divider" />}
            <FriendsRowList key="search">
              {this.buildUserRows(sortBy(belowUsers, 'sortOrder'))}
            </FriendsRowList>
          </AnimatedGroup>
        )}
      </div>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    invite: {
      position: 'relative',
      flexGrow: 1,
    },
    searchResults: {
      height: '100%',
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    divider: {
      height: 20,
      width: '100%',
      flexShrink: 0,
    },
    title: {
      fontSize: 30,
      fontWeight: 600,
      color: theme.color.basis2,
      textTransform: 'uppercase',
    },
    text: {
      fontSize: '1rem',
      fontWeight: 600,
      color: theme.color.basis3,
      width: '33%',
    },
    noUsers: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      color: theme.color.basis2,
      flexGrow: 1,
      overflow: 'hidden',
    },
    watermark: {
      color: theme.color.basis2,
      zIndex: 0,
      opacity: 0.1,
      left: 0,
      paddingBottom: '60%',
      width: '60%',
      boxSizing: 'content-box',
      transform: 'translate3d(-31%, 34%, 0)',
      // bottom: 0,
      // position: 'absolute',
      borderRadius: '100%',
      border: {
        color: theme.color.basis2,
        width: 35,
        style: 'solid',
      },
      [theme.mediaQuery.desktopAndUp]: {
        paddingBottom: '33%',
        width: '33%',
      },
      '& > svg': {
        position: 'absolute',
        left: 0,
        padding: 60,
      },
    },
    search: {
      backgroundColor: theme.color.basis6,
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      height: '100%',
    },
  };
}

function mapStateToProps(state: GlobalState, props: Props) {
  return {
    searchResults: searchFriendsSelector(state, props.searchId),
    isSearching: state.friendSearch.isSearchingUsers.has(props.searchId),
    isCreatingFriendRequests: state.friendRequests.isCreatingFriendRequests,
    friends: state.friends.friends,
    requests: state.friendRequests.friendRequests,
  };
}

export default connect(mapStateToProps, {
  onRemoveFriend: removeFriend,
})(useSheet(applyStyles)(GroupContentsSearchResults));
